<script setup>
import { ref, computed, watch } from 'vue'
import { useForm } from '@inertiajs/vue3'
import { PhCamera } from "@phosphor-icons/vue"
import Modal from '@pages/Shared/Modal.vue'
import Spinner from '@pages/Shared/ui/Spinner.vue'
import Input from '@pages/Shared/form/Input.vue'
import Select from '@pages/Shared/form/Select.vue'
import MultiSelect from '@pages/Shared/form/MultiSelect.vue'
import Textarea from '@pages/Shared/form/Textarea.vue'
import userMale from '@/../images/user-male.png'
import userFemale from '@/../images/user-female.png'

const props = defineProps({
    modelValue: Boolean,
    title:      String,
    status:     String,
    roles:      { type: Array, default: () => [] },
    classRooms: { type: Array, default: () => [] },
    subjects:   { type: Array, default: () => [] },
    staff:      { type: Object, default: null },
})

const emit = defineEmits(['update:modelValue', 'submitted'])

const fileInput = ref(null)

const form = useForm({
    _method:    null,
    id:         null,
    avatar:     null,
    preview:    null,
    name:       '',
    email:      '',
    gender:     null,
    dob:        '',
    phone:      '',
    address:    '',
    status:     null,
    roles:      [],
    classes:    [],
    subjects:   [],
})

const isFormMaster = computed(() =>
    props.roles.some(r =>
        r.label?.toLowerCase().includes('form master') && form.roles.includes(r.value)
    )
)
const isClassTeacher = computed(() =>
    props.roles.some(r =>
        r.label?.toLowerCase().includes('class teacher') && form.roles.includes(r.value)
    )
)

watch(() => props.staff, (staff) => {
    form.reset()
    form.clearErrors()
    if (!staff) return
    form.id       = staff.id         ?? null
    form.avatar   = staff.avatar     ?? null
    form.name     = staff.name       ?? ''
    form.email    = staff.email      ?? ''
    form.gender   = staff.gender     ?? null
    form.dob      = staff.dob        ?? ''
    form.phone    = staff.phone      ?? ''
    form.address  = staff.address    ?? ''
    form.status   = staff.status     ?? null
    form.roles    = staff.roles?.map(r => r.id)       ?? []
    form.classes  = staff.class_rooms?.map(c => c.id) ?? []  
    form.subjects = staff.subjects?.map(s => s.id)    ?? []
    form.preview  = null
}, { immediate: true, deep: true })

const handleFileSelected = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (form.preview) URL.revokeObjectURL(form.preview)
    form.avatar  = file
    form.preview = URL.createObjectURL(file)
}

const handleSubmit = () => emit('submitted', form)

const handleModalClose = () => {
    if (form.preview) URL.revokeObjectURL(form.preview)
    form.clearErrors()
    form.reset()
    emit('update:modelValue', false)
}
</script>

<template>
    <Modal
        :model-value="modelValue"
        :title="title"
        @update:modelValue="emit('update:modelValue', $event)"
        @close="handleModalClose"
    >
        <form class="p-4 md:p-5" @submit.prevent="handleSubmit">

            <!-- Avatar -->
            <div class="relative w-28 h-28 mx-auto my-4">
                <img
                    :src="form.preview
                        ? form.preview
                        : (form.avatar
                            ? `/storage/${form.avatar}`
                            : (form.gender === 'Male' ? userMale : userFemale))"
                    class="w-full h-full object-cover rounded-full ring-4 ring-gray-100"
                    alt="Staff avatar"
                />
                <span
                    v-if="status !== 'view'"
                    class="absolute bottom-0 right-0 p-1.5 rounded-full text-white cursor-pointer transition"
                    :class="status === 'edit'
                        ? 'bg-green-600 hover:bg-green-700'
                        : 'bg-primary-700 hover:bg-primary-800'"
                    @click="fileInput?.click()"
                >
                    <PhCamera :size="16" />
                </span>
                <input
                    ref="fileInput"
                    type="file"
                    accept="image/*"
                    class="hidden"
                    @change="handleFileSelected"
                />
            </div>

            <Input v-model="form.name" label="Full Name" placeholder="Enter full name"
                :disabled="status === 'view'" :error="form.errors.name" class="mb-4" />

            <Input v-model="form.email" label="Email" type="email" placeholder="Email"
                :disabled="status === 'view'" :error="form.errors.email" class="mb-4" />

            <Select label="Gender" v-model="form.gender" :options="['Male', 'Female']"
                placeholder="Select Gender" :disabled="status === 'view'"
                :error="form.errors.gender" class="mb-4" />

            <Input v-model="form.dob" type="date" label="Date of Birth"
                :disabled="status === 'view'" :error="form.errors.dob" class="mb-4" />

            <Input v-model="form.phone" type="text" label="Phone" placeholder="Phone Number"
                :disabled="status === 'view'" :error="form.errors.phone" class="mb-4" />

            <Textarea label="Address" v-model="form.address" placeholder="Enter address..."
                :rows="2" :disabled="status === 'view'" :error="form.errors.address" class="mb-4" />

            <MultiSelect v-model="form.roles" :options="roles" label="Roles"
                placeholder="Select role" class="mb-4" :disabled="status === 'view'" />

            <MultiSelect v-if="isFormMaster" label="Class Room(s)" v-model="form.classes"
                :options="classRooms" placeholder="Select class(es)"
                :disabled="status === 'view'" :error="form.errors.classes" class="mb-4" />

            <MultiSelect v-if="isClassTeacher" v-model="form.subjects" :options="subjects"
                label="Subjects" placeholder="Select subjects"
                :disabled="status === 'view'" :error="form.errors.subjects" class="mb-4" />

            <Select v-if="status !== 'create'" label="Status" v-model="form.status"
                :options="['Active', 'Inactive']" placeholder="Select status"
                :disabled="status === 'view'" :error="form.errors.status" class="mb-4" />

            <Input v-model="form.id" type="text" label="Staff ID"
                :disabled="true" :error="form.errors.id" class="mb-4" />

            <button
                v-if="status !== 'view'"
                type="submit"
                class="w-full px-6 py-3 rounded-lg text-white text-sm font-semibold
                       transition flex justify-center items-center gap-2"
                :class="status === 'edit'
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-primary-700 hover:bg-primary-800'"
                :disabled="form.processing"
            >
                <Spinner v-if="form.processing" :size="14" />
                {{ status === 'edit' ? 'Update Staff' : 'Add Staff' }}
            </button>

        </form>
    </Modal>
</template>